import { useEffect, useRef, useState } from "react";
import {
  ApiError,
  api,
  type TelegramEstado,
  type TelegramVinculo,
} from "@/lib/api";
import ConfirmDialog from "@/ui/ConfirmDialog";
import { useToast } from "@/ui/Toast";

/**
 * Vinculación de la cuenta con Telegram desde el Perfil. El usuario NUNCA
 * teclea su chat ID: el deep-link t.me/<bot>?start=CODIGO hace todo. Mientras
 * hay un código pendiente se hace polling suave del estado para confirmar
 * solo cuando el bot complete la vinculación.
 *
 * El interruptor para prender/apagar el canal de Telegram vive arriba, en
 * "Notificaciones" (centro de canales); aquí solo se administra la conexión.
 */
export default function TelegramPanel() {
  const toast = useToast();
  const [estado, setEstado] = useState<TelegramEstado | null>(null);
  const [vinculo, setVinculo] = useState<TelegramVinculo | null>(null);
  const [trabajando, setTrabajando] = useState(false);
  const [confirmDesvincular, setConfirmDesvincular] = useState(false);
  const pollRef = useRef<number | null>(null);

  async function cargarEstado(): Promise<TelegramEstado | null> {
    try {
      const e = await api.get<TelegramEstado>("/api/telegram/estado");
      setEstado(e);
      return e;
    } catch {
      return null;
    }
  }

  useEffect(() => {
    void cargarEstado();
    return () => {
      if (pollRef.current) window.clearInterval(pollRef.current);
    };
  }, []);

  function empezarPolling() {
    if (pollRef.current) window.clearInterval(pollRef.current);
    let intentos = 0;
    pollRef.current = window.setInterval(async () => {
      intentos += 1;
      const e = await cargarEstado();
      if (e?.vinculado || intentos > 45) {
        if (pollRef.current) window.clearInterval(pollRef.current);
        pollRef.current = null;
        if (e?.vinculado) {
          setVinculo(null);
          // Vincular prende solo el canal (api/telegram.py: notif_telegram =
          // True). Antes este aviso pedía activarlo a mano: no hacía falta.
          toast.success(
            "Telegram vinculado",
            "Ya te llegan avisos ahí; el canal se pausa en la sección Notificaciones.",
          );
        }
      }
    }, 4000);
  }

  async function vincular() {
    setTrabajando(true);
    try {
      const v = await api.post<TelegramVinculo>("/api/telegram/vincular", {});
      setVinculo(v);
      empezarPolling();
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude generar el enlace", msg);
    } finally {
      setTrabajando(false);
    }
  }

  async function desvincular() {
    if (pollRef.current) {
      window.clearInterval(pollRef.current);
      pollRef.current = null;
    }
    try {
      await api.del("/api/telegram/vincular");
      setConfirmDesvincular(false);
      setVinculo(null);
      await cargarEstado();
      toast.success("Telegram desvinculado");
    } catch (err) {
      const msg = err instanceof ApiError ? err.detail : "Error";
      toast.error("No pude desvincular", msg);
    }
  }

  return (
    <div className="card space-y-3">
      <div>
        <h2 className="text-sm font-medium text-zinc-300">Telegram</h2>
        <p className="text-xs text-zinc-500 mt-1">
          Vincula tu cuenta para poder recibir avisos en tu chat de Telegram, sin
          depender del navegador. Es de un toque — no necesitas saber ningún dato
          de tu cuenta. El interruptor para prender o apagar este canal está
          arriba, en <strong>Notificaciones</strong>.
        </p>
      </div>

      {estado === null ? (
        <p className="text-xs text-zinc-500">Cargando…</p>
      ) : !estado.configurado ? (
        <p className="text-xs text-zinc-500">
          El bot de Telegram aún no está configurado. Pídele al administrador
          que cargue el token en Configuración → Integraciones → Bot de Telegram.
        </p>
      ) : estado.vinculado ? (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <span className="text-[10px] uppercase tracking-wide px-2 py-0.5 rounded border bg-emerald-500/10 text-emerald-300 border-emerald-500/30 w-fit">
            vinculado{estado.bot_username ? ` · @${estado.bot_username}` : ""}
          </span>
          <button
            onClick={() => setConfirmDesvincular(true)}
            className="btn-ghost text-sm w-full sm:w-auto text-red-400/80 hover:text-red-300"
          >
            Desvincular
          </button>
        </div>
      ) : vinculo ? (
        <div className="space-y-2">
          <a
            href={vinculo.deep_link}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-primary text-sm w-full sm:w-auto inline-block text-center"
          >
            Abrir Telegram y vincular
          </a>
          <p className="text-xs text-zinc-500">
            Se abre el chat del bot — solo toca <strong>INICIAR</strong>. Si no
            se abre, mándale{" "}
            <code className="rounded bg-ink-950 border border-ink-700 px-1.5 py-0.5 font-mono text-accent-300">
              /atvincular {vinculo.codigo}
            </code>{" "}
            a @{estado.bot_username}. El código vence en {vinculo.expira_en_min}{" "}
            minutos. Esta pantalla se actualiza sola al completarse.
          </p>
        </div>
      ) : (
        <button
          onClick={() => void vincular()}
          disabled={trabajando}
          className="btn-primary text-sm w-full sm:w-auto disabled:opacity-50"
        >
          {trabajando ? "Generando…" : "Vincular Telegram"}
        </button>
      )}

      {confirmDesvincular && (
        <ConfirmDialog
          open={true}
          onOpenChange={(v) => !v && setConfirmDesvincular(false)}
          title="Desvincular Telegram"
          description="Dejarás de recibir recordatorios y avisos por Telegram. Puedes volver a vincular cuando quieras."
          confirmLabel="Desvincular"
          onConfirm={() => void desvincular()}
        />
      )}
    </div>
  );
}
